/**
 * Files the app was opened with: "Open with" on a file, once the app is
 * installed (the manifest's `file_handlers`). The browser hands them over
 * through `launchQueue`, as writable handles, which is the web's counterpart
 * of the desktop's command-line paths.
 *
 * As on the desktop, files that arrive before the page is listening are held
 * until it asks for them, and later ones are sent straight on.
 */
import { pathForFileHandle } from './handles'

type Listener = (path: string) => void

const pending: string[] = []
const listeners = new Set<Listener>()
let taken = false

interface LaunchParamsLike {
  files?: FileSystemHandle[]
}

async function receive(params: LaunchParamsLike): Promise<void> {
  for (const handle of params.files ?? []) {
    if (handle.kind !== 'file') continue
    const path = await pathForFileHandle(handle as FileSystemFileHandle)
    if (taken && listeners.size > 0) for (const fn of listeners) fn(path)
    else pending.push(path)
  }
}

export function initLaunchQueue(): void {
  const queue = (
    window as { launchQueue?: { setConsumer(fn: (p: LaunchParamsLike) => void): void } }
  ).launchQueue
  queue?.setConsumer((params) => void receive(params))
}

/** The files held so far; from now on, new ones go to the listeners. */
export function takeLaunchFiles(): string[] {
  taken = true
  return pending.splice(0)
}

export function onLaunchFile(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
